import { DATE_FORMAT } from '@components/InputCalendar/constants';
import type { AutoPopulateFromConfig, AutoPopulateOffset, JSONSchema } from '@root/@types/schema';
import dayjs from 'dayjs';
import { get, set } from 'lodash-es';

const DEFAULT_OFFSET: AutoPopulateOffset = { years: 1 };

export interface AutoPopulateRule {
  sourcePathPattern: string;
  targetPathPattern: string;
  offset: AutoPopulateOffset;
}

function normalizeConfig(raw: string | AutoPopulateFromConfig): AutoPopulateFromConfig {
  return typeof raw === 'string' ? { field: raw } : raw;
}

function walkSchemaNode(node: Record<string, any>, currentPath: string, rules: AutoPopulateRule[]): void {
  if (!node || typeof node !== 'object') return;

  if (node['ui:autoPopulateFrom']) {
    const config = normalizeConfig(node['ui:autoPopulateFrom']);
    // `config.field` is relative to the target's parent — segment count determines how far we climb.
    const fieldSegments = config.field.split('.');
    const targetSegments = currentPath.split('.');
    const basePath = targetSegments.slice(0, -fieldSegments.length).join('.');
    const sourcePath = basePath ? `${basePath}.${config.field}` : config.field;

    rules.push({
      sourcePathPattern: sourcePath,
      targetPathPattern: currentPath,
      offset: config.offset ?? DEFAULT_OFFSET,
    });
  }

  if (node.properties) {
    for (const [key, value] of Object.entries(node.properties as Record<string, Record<string, any>>)) {
      walkSchemaNode(value, currentPath ? `${currentPath}.${key}` : key, rules);
    }
  }

  if (node.type === 'array' && node.items) {
    walkSchemaNode(node.items as Record<string, any>, `${currentPath}[]`, rules);
  }
}

export function buildAutoPopulateRules(schema: JSONSchema): AutoPopulateRule[] {
  const rules: AutoPopulateRule[] = [];
  const submissionForm = schema?.properties?.SubmissionForm;
  if (!submissionForm?.properties) return rules;

  for (const [key, value] of Object.entries(submissionForm.properties as Record<string, Record<string, any>>)) {
    walkSchemaNode(value, key, rules);
  }

  return rules;
}

function toSchemaPattern(formPath: string): string {
  return formPath.replace(/\[\d+\]/g, '[]');
}

// Invariant: source and target patterns must contain the same `[]` placeholders in the same order — indices are matched positionally.
function toFormPath(schemaPattern: string, referenceFormPath: string): string {
  const indices = [...referenceFormPath.matchAll(/\[(\d+)\]/g)].map((m) => m[1]);
  let i = 0;
  return schemaPattern.replace(/\[\]/g, () => `[${indices[i++]}]`);
}

export function applyAutoPopulateRules(
  rules: AutoPopulateRule[],
  formValues: Record<string, any>,
  changedFieldName: string,
): void {
  const changedPattern = toSchemaPattern(changedFieldName);

  for (const rule of rules) {
    if (changedPattern !== rule.sourcePathPattern) continue;

    const targetPath = toFormPath(rule.targetPathPattern, changedFieldName);
    if (get(formValues, targetPath)) continue;

    const sourceValue = get(formValues, changedFieldName);
    if (!sourceValue || typeof sourceValue !== 'string') continue;

    const parsed = dayjs(sourceValue);
    if (!parsed.isValid()) continue;

    const { years, months, days } = rule.offset;
    let result = parsed;
    if (years) result = result.add(years, 'year');
    if (months) result = result.add(months, 'month');
    if (days) result = result.add(days, 'day');

    set(formValues, targetPath, result.format(DATE_FORMAT));
  }
}
